<template>
  <div class="step-progress-tracker mb-4">
    <div class="progress-container">
      <div class="progress-bar-bg">
        <div 
          class="progress-bar-fill" 
          :style="{ width: progressPercentage + '%' }"
        ></div>
      </div>
      
      <div class="steps-container">
        <div 
          v-for="(step, index) in steps" 
          :key="step.key"
          class="step-item"
          :class="{
            'completed': step.completed,
            'current': step.current,
            'accessible': step.accessible,
            'locked': !step.accessible
          }"
          @click="navigateToStep(step)"
        >
          <div class="step-circle">
            <i v-if="step.completed" class="fas fa-check"></i>
            <i v-else-if="step.current" class="fas fa-edit"></i>
            <span v-else>{{ index + 1 }}</span>
          </div>
          
          <div class="step-content">
            <div class="step-title">{{ step.title }}</div>
            <div class="step-subtitle">{{ step.subtitle }}</div>
            <div v-if="step.completedAt" class="step-timestamp">
              <i class="fas fa-clock me-1"></i>
              {{ formatDate(step.completedAt) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="progress-summary mt-3">
      <div class="row g-3">
        <div class="col-md-3">
          <div class="summary-card">
            <div class="summary-icon">
              <i class="fas fa-tasks text-primary"></i>
            </div>
            <div class="summary-content">
              <div class="summary-value">{{ completedSteps }}/{{ totalSteps }}</div>
              <div class="summary-label">Étapes complétées</div>
            </div>
          </div>
        </div>
        
        <div class="col-md-3">
          <div class="summary-card">
            <div class="summary-icon">
              <i class="fas fa-percentage text-success"></i>
            </div>
            <div class="summary-content">
              <div class="summary-value">{{ progressPercentage }}%</div>
              <div class="summary-label">Progression</div>
            </div>
          </div>
        </div>
        
        <div class="col-md-3">
          <div class="summary-card">
            <div class="summary-icon">
              <i class="fas fa-arrow-right text-info"></i>
            </div>
            <div class="summary-content">
              <div class="summary-value">{{ nextStepTitle || 'Terminé' }}</div>
              <div class="summary-label">Prochaine étape</div>
            </div>
          </div>
        </div>
        
        <div class="col-md-3">
          <div class="summary-card">
            <div class="summary-icon">
              <i class="fas fa-save text-warning"></i>
            </div>
            <div class="summary-content">
              <div class="summary-value">{{ canSaveDraft ? 'Oui' : 'Non' }}</div>
              <div class="summary-label">Sauvegarde possible</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue'
import { useRouter } from 'vue-router'
import { bescWizard, calculateProgressionPercentage, getNextStep, canAccessStep } from '@/utils/bescWizard'

interface StepInfo {
  key: string
  title: string
  subtitle: string
  route: string
  completed: boolean
  current: boolean
  accessible: boolean
  completedAt?: string
}

export default defineComponent({
  name: 'StepProgressTracker',
  props: {
    currentStep: {
      type: String as PropType<'step1' | 'step2' | 'step3' | 'final'>,
      required: true
    },
    bescId: {
      type: String,
      required: true
    }
  },
  
  setup(props) {
    const router = useRouter()
    
    const steps = computed((): StepInfo[] => {
      const progression = bescWizard.progression
      
      return [
        {
          key: 'step1',
          title: 'Informations générales',
          subtitle: 'Parties prenantes et transport',
          route: 'AddBescStep1',
          completed: progression.step1Completed,
          current: props.currentStep === 'step1',
          accessible: canAccessStep('step1'),
          completedAt: progression.step1_completed_at || undefined
        },
        {
          key: 'step2',
          title: 'Bills of Lading',
          subtitle: 'BLs et marchandises',
          route: 'AddBlStep2',
          completed: progression.step2Completed,
          current: props.currentStep === 'step2',
          accessible: canAccessStep('step2'),
          completedAt: progression.step2_completed_at || undefined
        },
        {
          key: 'step3',
          title: 'Documents',
          subtitle: 'Pièces justificatives',
          route: 'AddBescStep3',
          completed: progression.step3Completed,
          current: props.currentStep === 'step3',
          accessible: canAccessStep('step3'),
          completedAt: progression.step3_completed_at || undefined
        },
        {
          key: 'final',
          title: 'Récapitulatif',
          subtitle: 'Validation et soumission',
          route: 'Recapitulatif',
          completed: progression.recap_completed,
          current: props.currentStep === 'final',
          accessible: canAccessStep('final'),
          completedAt: progression.final_submitted_at || undefined
        }
      ]
    })
    
    const progressPercentage = computed(() => calculateProgressionPercentage())
    
    const completedSteps = computed(() => 
      steps.value.filter(step => step.completed).length
    )
    
    const totalSteps = computed(() => steps.value.length)
    
    const nextStepTitle = computed(() => {
      const nextStep = steps.value.find(step => !step.completed && step.accessible)
      return nextStep?.title || null
    })
    
    const canSaveDraft = computed(() => {
      return bescWizard.progression.step1Completed
    })
    
    const navigateToStep = (step: StepInfo) => {
      if (!step.accessible) {
        return
      }
      
      router.push({ 
        name: step.route, 
        params: { id: props.bescId } 
      })
    }
    
    const formatDate = (dateStr: string) => {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    
    return {
      steps,
      progressPercentage,
      completedSteps,
      totalSteps,
      nextStepTitle,
      canSaveDraft,
      navigateToStep,
      formatDate
    }
  }
})
</script>

<style scoped>
.step-progress-tracker {
  background: white;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e9ecef;
}

.progress-container {
  position: relative;
  margin-bottom: 0;
}

.progress-bar-bg {
  height: 2px;
  background-color: #e9ecef;
  border-radius: 1px;
  position: relative;
  margin: 0.5rem 0;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #0a59a4 0%, #28a745 100%);
  border-radius: 1px;
  transition: width 0.6s ease;
}

.steps-container {
  display: flex;
  justify-content: space-between;
  position: relative;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0.25rem;
  border-radius: 4px;
}

.step-item:hover:not(.locked) {
  background-color: #f8f9fa;
}

.step-item.locked {
  cursor: not-allowed;
  opacity: 0.5;
}

.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
  transition: all 0.2s ease;
  border: 2px solid #e9ecef;
  background-color: white;
  color: #6c757d;
}

.step-item.completed .step-circle {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.step-item.current .step-circle {
  background-color: #0a59a4;
  border-color: #0a59a4;
  color: white;
  animation: pulse 2s infinite;
}

.step-item.accessible:not(.completed):not(.current) .step-circle {
  border-color: #0a59a4;
  color: #0a59a4;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(10, 89, 164, 0.5);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(10, 89, 164, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(10, 89, 164, 0);
  }
}

.step-content {
  min-height: 35px;
}

.step-title {
  font-weight: 600;
  font-size: 0.75rem;
  color: #212529;
  margin-bottom: 0.1rem;
  line-height: 1.2;
}

.step-subtitle {
  font-size: 0.65rem;
  color: #6c757d;
  margin-bottom: 0.15rem;
  line-height: 1.2;
}

.step-timestamp {
  font-size: 0.6rem;
  color: #28a745;
  font-weight: 500;
}

.step-item.locked .step-title,
.step-item.locked .step-subtitle {
  color: #adb5bd;
}

.progress-summary {
  border-top: 1px solid #e9ecef;
  padding-top: 0.5rem;
  margin-top: 0.25rem;
}

.summary-card {
  display: flex;
  align-items: center;
  padding: 0.35rem 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 2px solid #0a59a4;
  transition: all 0.2s ease;
}

.summary-card:hover {
  background-color: #e9ecef;
}

.summary-icon {
  margin-right: 0.5rem;
  font-size: 0.9rem;
}

.summary-content {
  flex: 1;
}

.summary-value {
  font-size: 0.85rem;
  font-weight: 700;
  color: #212529;
  margin-bottom: 0;
  line-height: 1.2;
}

.summary-label {
  font-size: 0.65rem;
  color: #6c757d;
  font-weight: 500;
  line-height: 1.2;
}

@media (max-width: 768px) {
  .steps-container {
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .step-item {
    flex-direction: row;
    text-align: left;
    justify-content: flex-start;
  }
  
  .step-circle {
    margin-right: 0.5rem;
    margin-bottom: 0;
    width: 26px;
    height: 26px;
    font-size: 0.7rem;
  }
  
  .step-content {
    min-height: auto;
  }
  
  .progress-bar-bg {
    display: none;
  }
}
</style>